import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { IndividualService } from '../../../../Component/Api/Individual/IndividualService';
import { ConfirmationDialog } from '../../../../Component/Common/Components/ConfirmationDialog';
import { DataTable } from '../../../../Component/Common/Components/DataTable/DataTable';
import { EE123PeopleRole_Edit, PeopleEditRole } from '../../../../Component/Common/Constants/UserRoles';
import { Individual } from '../../../../Component/Model/Dto/Individual';
import { EntriesPerPage, IBulkActions } from '../../../../Component/Model/Table/Table';
import { userRolesState } from '../../../../State/user.Atom';
import { EmploymentDataModal } from './EmploymentDataModal';
import { EmploymentDataTableMapper } from './EmploymentTableColumns';
import { UserActivityService } from '../../../../Component/Api/UserActivity/UserActivityService';
import { CDMSummariesPeopleSection } from '../../../../Component/Common/Constants/UserActivityTrackingSections';
import { UserActivityActionType } from '../../../../Component/Model/Enum/BEE123Enums';

interface IProps {
    scorecardId: string;
    individualId: number;
    section?: string;
}

export const EmploymentDataTable = (props: IProps) => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [employmentData, setEmploymentData] = useState<Individual.EmploymentDataRowDto[]>([]);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [employeeDataId, setEmployeeDataId] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>('');
    const [showEmploymentDataModal, setShowEmploymentDataModal] = useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);

    const editRoleAccess = [
        EE123PeopleRole_Edit
    ]

    const userRoles = useRecoilValue(userRolesState);
    const isEditable = userRoles.some(r => editRoleAccess.includes(r));

    const employmentDataSearchRequest: Individual.EmploymentDataSearchRequest = {
        PageNumber: pageNumber as number,
        PageSize: entriesPerPage.defaultValue as number,
        ScorecardId: props.scorecardId,
        SearchValue: searchValue,
        Draw: 2,
        IndividualId: props.individualId
    };

    const loadEmploymentData = async () => {
        setIsLoading(true);
        try {
            const result = await IndividualService.SearchIndividualEmploymentData(employmentDataSearchRequest);
            setFilteredItemsCount(result.data.filteredItemsCount);
            setTotalItemsCount(result.data.totalItemsCount);
            setEmploymentData(result.data.dataObject);

        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    }

    const onAddEmploymentData = () => {
        setShowEmploymentDataModal(true);
    }


    const onEdit = (id: number) => {
        setEmployeeDataId(id);
        setShowEmploymentDataModal(true);
        props.section && UserActivityService.createActivityLog({
            section: props.section,
            actionType: UserActivityActionType.Update,
            actionDescription: "Update employment data",
            scorecardId: props.section === CDMSummariesPeopleSection ? undefined : props.scorecardId,
            requestUrl: window.location.href
        });
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setEmployeeDataId(0);
    }

    const handleConfirmDelete = (id: number) => {
        setEmployeeDataId(id);
        setShowConfirmationDialog(true);
    }

    const onDelete = async () => {
        handleCloseConfirmation();

        const employmentDataDeleteRequest: Individual.DeleteEmploymentDataRequest = {
            id: employeeDataId,
            isAuditRow: false,
            scorecardId: props.scorecardId
        };

        try {
            await IndividualService.DeleteIndividualEmploymentData(employmentDataDeleteRequest);
            toast.success("Employment data entry successfully deleted");
        } catch (error) {
            console.log(error);
        } finally {
            loadEmploymentData();
        }
    }

    const closeEmploymentModal = () => {
        setEmployeeDataId(0);
        loadEmploymentData();
        setShowEmploymentDataModal(false);
    }

    useEffect(() => {
        loadEmploymentData();
    }, [entriesPerPage, pageNumber, searchValue]);

    const bulkActions: IBulkActions[] = [
    ]

    if (isEditable) {
        bulkActions.push({
            label: 'Capture New Employment Data',
            action: onAddEmploymentData,
        });
    }

    const employmentTableData = EmploymentDataTableMapper(employmentData, onEdit, handleConfirmDelete);

    return (
        <>
            <DataTable
                table={employmentTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                canSearch={true}
                searchValueChanged={searchValueChanged}
                bulkActions={bulkActions}
            ></DataTable>

            {showEmploymentDataModal && <EmploymentDataModal id={employeeDataId} individualId={props.individualId} onSaved={setEmployeeDataId} showEmploymentDataModal={showEmploymentDataModal} closeEmploymentModal={closeEmploymentModal} hasEmployeeDataEntries={!!employmentTableData} />}

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={onDelete}
                handleCancel={handleCloseConfirmation}
                heading={"Delete Entry"}
                message={"Are you sure you want to delete the selected entry?"}
                isLoading={isLoading}
            />
        </>
    );
}